/**
 * Cloud History API Client
 *
 * Provides methods to interact with the cloud history backend (DynamoDB + S3).
 * All methods require authentication via UserStore authData.
 *
 * Backend API endpoints:
 * - GET    /api/history                - List history items with pagination
 * - POST   /api/history               - Create new history item (type in body)
 * - GET    /api/history/:id?type=...  - Get full item with turns
 * - PUT    /api/history/:id?type=...  - Update existing item
 * - DELETE /api/history/:id?type=...  - Delete item (soft delete; '*' deletes all of type)
 */

import { getConfig } from "../lib/configMgr";
import { toJSON, RequestBuilder } from "./request";

const { ExternalRESTAPI } = getConfig();

/**
 * List history items with pagination
 *
 * @param {Object} authData - User authentication data from UserStore
 * @param {Object} options - Query options
 * @param {string} options.type - 'ALL', 'CONVERSATION', or 'SEARCH'
 * @param {number} options.limit - Max items to return (default: 50, max: 100)
 * @param {string} options.before - ISO timestamp cursor for pagination
 * @param {string} options.cell - Optional cell filter
 * @param {boolean} options.includeTurns - Include full turn data (conversations only)
 * @returns {Promise<{items: Array, nextCursor: string|null}>}
 */
export const listHistory = async (authData, {
  type = 'ALL',
  limit = 50,
  before = null,
  cell = null,
  includeTurns = false,
} = {}) => {
  const url = new URL(ExternalRESTAPI.History, window.location.origin);

  url.searchParams.set('type', type);
  url.searchParams.set('limit', limit.toString());
  url.searchParams.set('include_turns', includeTurns.toString());

  if (before) url.searchParams.set('before', before);
  if (cell) url.searchParams.set('cell', cell);

  return new RequestBuilder(url)
    .withAuthorization(authData)
    .fetch()
    .then(toJSON);
};

/**
 * Get a single history item by ID
 *
 * @param {Object} authData - User authentication data
 * @param {string} itemId - Item UUID (or URL for searches)
 * @param {string} type - 'conversation' or 'search'
 * @param {boolean} includeTurns - Include turn data (default: true)
 * @returns {Promise<Object>} Full item object
 */
export const getHistoryItem = async (authData, itemId, type, includeTurns = true) => {
  const url = new URL(
    `${ExternalRESTAPI.History}/${encodeURIComponent(itemId)}`,
    window.location.origin
  );

  url.searchParams.set('type', type);
  url.searchParams.set('include_turns', includeTurns.toString());

  return new RequestBuilder(url)
    .withAuthorization(authData)
    .fetch()
    .then(response => response.status === 204 ? null : toJSON(response));
};

/**
 * Create a new history item
 *
 * @param {Object} authData - User authentication data
 * @param {string} type - 'conversation' or 'search'
 * @param {Object} itemData - Item data (id, context, turns, etc.)
 * @returns {Promise<Object>} Created item with id and timestamps
 */
export const createHistoryItem = async (authData, type, itemData) => {
  const url = new URL(ExternalRESTAPI.History, window.location.origin);

  return new RequestBuilder(url)
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify({ ...itemData, type }))
    .fetch()
    .then(toJSON);
};

/**
 * Update an existing history item
 *
 * Uses PUT to update the item. The backend merges the provided fields
 * with the existing item data.
 *
 * @param {Object} authData - User authentication data
 * @param {string} itemId - Item UUID
 * @param {string} type - 'conversation' or 'search'
 * @param {Object} itemData - Updated item data
 * @returns {Promise<Object>} Updated item
 */
export const updateHistoryItem = async (authData, itemId, type, itemData) => {
  const url = new URL(
    `${ExternalRESTAPI.History}/${encodeURIComponent(itemId)}`,
    window.location.origin
  );

  url.searchParams.set('type', type);

  return new RequestBuilder(url)
    .withAuthorization(authData)
    .withJSONContentType()
    .asPUT(JSON.stringify(itemData))
    .fetch()
    .then(toJSON);
};

/**
 * Delete a history item (soft delete, 30 day retention)
 *
 * @param {Object} authData - User authentication data
 * @param {string} itemId - Item UUID
 * @param {string} type - 'conversation' or 'search'
 * @returns {Promise<void>}
 */
export const deleteHistoryItem = async (authData, itemId, type) => {
  const url = new URL(
    `${ExternalRESTAPI.History}/${encodeURIComponent(itemId)}`,
    window.location.origin
  );

  url.searchParams.set('type', type);

  return new RequestBuilder(url)
    .withAuthorization(authData)
    .asDELETE()
    .fetch();
};

/**
 * Delete all history items of a given type (soft delete)
 *
 * @param {Object} authData - User authentication data
 * @param {string} type - 'conversation' or 'search'
 * @returns {Promise<void>}
 */
export const deleteAllHistoryByType = async (authData, type) => {
  const url = new URL(
    `${ExternalRESTAPI.History}/*`,
    window.location.origin
  );

  url.searchParams.set('type', type);

  return new RequestBuilder(url)
    .withAuthorization(authData)
    .asDELETE()
    .fetch();
};